import React, {ReactNode} from "react";
import PortalDocument, {PortalDocumentInterface} from "../document/PortalDocument";
import Downloader from "../document/Downloader";
import DocumentSettings from "../document/DocumentSettings";
import ContentMode from "../ContentMode";
import SidebarLeftMode from "../viewer/settings/SidebarLeftMode";
import SidebarRightMode from "../viewer/settings/SidebarRightMode";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import {PdfaDialogDefinition} from "../dialog/components/convert/PdfaDialog";
import {Tooltip} from "primereact/tooltip";
import DomHelper from "../../shared/helper/DomHelper";
import {
	Description,
	DocumentFile,
	Information,
	MetadataViewerPreferences
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../PortalContext";
import MenuButton from "../../shared/menu/MenuButton";
import Immutable from "../../shared/immutable/Immutable";
import PortalDocumentManager, {PortalDocumentManagerGlobals} from "../document/PortalDocumentManager";
import DocumentSorting, {DocumentSortingGlobals} from "../document/DocumentSorting";
import {DeleteDialogDefinition} from "../dialog/components/document/DeleteDialog";
import Parameter from "../dialog/parameter/Parameter";
import {DeleteDocumentType} from "../../shared/models/DeleteDocumentType";
import Translate from "../../shared/translation/Translate";
import {SidebarFunctionGlobals} from "../document/components/pdf/SidebarFunctions";
import Icon from "../../shared/components/Icon";
import {DebugGlobals} from "../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import Preview from "../startscreen/Preview";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

interface DocumentBarItemProperties extends I18nGlobals, PortalDocumentManagerGlobals, DocumentSortingGlobals,
	SidebarFunctionGlobals, DebugGlobals {
	id?: string;
	context: PortalContext;
	contentMode: ContentMode;
	portalDocument?: Immutable<PortalDocumentInterface>;
}

export class DocumentBarItemDefinition extends PortalPureComponent<DocumentBarItemProperties, any> {
	constructor(props: Readonly<DocumentBarItemProperties>) {
		super(props);

		this.onSelect = this.onSelect.bind(this);
		this.closeDocument = this.closeDocument.bind(this);
	}

	/**
	 * called on selecting a document
	 */
	private async onSelect(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		if (this.props.portalDocument!.get().settings.selected && this.props.contentMode === ContentMode.VIEWER) {
			return;
		}

		let command: () => Promise<void> = async () => {
			this.props.context.documentManager.open(this.props.portalDocument!.get().id!);
		}

		if (DocumentSettings.checkActiveMouseFunction(this.props)) {
			DocumentSettings.confirmResetMouseFunction(this.props, command);
			return;
		}

		await command();
	}

	private closeDocument(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let documentSelection: Array<PortalDocumentInterface> = this.props.documentSelection.get();
		let documentSorting: DocumentSorting = DocumentSorting.fromJson(this.props.documentSorting.get());
		let sortedItems: Array<PortalDocumentInterface> = documentSorting.sortItems(this.props.portalDocuments.get());

		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		documentSettings.closed = true;

		if (documentSettings.selected) {
			documentManager.unselect(this.props.portalDocument!.get().id!);

			if (documentSelection.length === 1 && this.props.contentMode === ContentMode.VIEWER) {
				for (let item of sortedItems) {
					if (!item.settings.closed && !item.settings.selected) {
						documentManager.open(item.id!);
						break;
					}
				}
			}
		}

		documentManager.updateGlobalDocumentList();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let tooltipId: string = DomHelper.generateUUID(true);
		let portalInfo: Information = this.props.context.info;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		let documentFile: DocumentFile | undefined = portalDocument.getDocumentFile();
		if (!FunctionHelper.exists(documentFile)) {
			return null;
		}

		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let downloader: Downloader = new Downloader(this.props.context.session.getToken()!);

		let documentTitle: string = FunctionHelper.applyValue(
			portalDocument.metadata?.information?.description?.title, Description.getTitleDefault()
		);
		let displayDocumentTitle: boolean =
			FunctionHelper.applyValue(
				portalDocument.metadata?.information?.viewerPreferences?.displayDocTitle,
				MetadataViewerPreferences.getDisplayDocTitleDefault()
			) && documentTitle.trim() !== "";

		let classList: Array<string> = ["document-bar-item"];

		if (!portalDocument.isLocked()) {
			classList.push("preview-" + tooltipId);
		}

		if (documentSettings.selected) {
			classList.push("selected");
		}

		return (
			<div className={classList.join(" ")} id={this.props.id} onClick={this.onSelect}
				 data-pr-position={"bottom"}>
				<div className="column column-download">
					<MenuButton {...this.props} icon="icon icon-download small"
								disabled={portalDocument.isLocked()}
								onClick={async (event: React.MouseEvent) => {
									event.stopPropagation(); // stop document selection

									if (!FunctionHelper.exists(this.props.portalDocument) ||
										!FunctionHelper.exists(this.props.portalDocument!.value)) {
										return;
									}

									await downloader.downloadDocument(
										this.props.portalDocument!.get().id!,
										FunctionHelper.applyValue(
											documentFile?.fileName, DocumentFile.getFileNameDefault()
										)
									);
								}}
								tooltip={Translate.value("document_bar.download", {ns: "menu"})}/>
				</div>
				<div className={"column column-name"} data-pr-tooltip={
					displayDocumentTitle ?
						documentTitle :
						documentFile!.fileName + (
							!FunctionHelper.isEmpty(documentFile!.fileExtension) ?
								"." + documentFile!.fileExtension : ""
						)
				}>
					{displayDocumentTitle ?
						<span className="filename">{documentTitle}</span> :
						<>
							<span className="filename">{documentFile!.fileName}</span>
							<span className="fileextension">.{documentFile!.fileExtension}</span>
						</>
					}
				</div>
				<div className="column column-options">
					{portalDocument.isArchive() && !portalDocument.isPortfolio() &&
						<MenuButton {...this.props} icon="icon icon-archive small"
									tooltip={Translate.value("document_bar.archive", {ns: "menu"})}
									onClick={async (event: React.MouseEvent) => {
										event.stopPropagation(); // stop document selection

										if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
											return;
										}

										let command: () => Promise<void> = async () => {
											documentManager.open(this.props.portalDocument!.get()?.id!);

											if (PdfaDialogDefinition.isActive(
												[portalDocument], this.props.context.info,
												this.props.signatureEditProfile
											)) {
												this.props.context.getPage().setState({
													showPdfaDialog: true,
													pdfaParameter: undefined
												});
											}
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}>
							{this.props.portalDocument!.get().archiveType}
						</MenuButton>
					}
					{portalDocument.hasSignature() && !portalDocument.isPortfolio() &&
						<MenuButton {...this.props} icon="icon icon-signature small"
									tooltip={Translate.value("document_bar.signature", {ns: "menu"})}
									onClick={async (event: React.MouseEvent) => {
										event.stopPropagation(); // stop document selection

										if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
											return;
										}

										let command: () => Promise<void> = async () => {
											documentSettings.sidebarLeftMode = SidebarLeftMode.SIGNATURES;
											documentManager.open(this.props.portalDocument!.get().id!);
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}>
							{this.props.portalDocument!.get().signatures}
						</MenuButton>
					}
					{(portalDocument.hasPasswordProtection() || portalDocument.hasCertificateProtection()) &&
						<MenuButton {...this.props} icon="icon icon-locked"
									tooltip={
										portalDocument.hasPasswordProtection() ?
											Translate.value(
												"document_bar.security.password", {ns: "menu"}
											) :
											portalDocument.hasCertificateProtection() ?
												Translate.value(
													"document_bar.security.certificate", {ns: "menu"}
												) :
												undefined
									}
									onClick={async (event: React.MouseEvent) => {
										event.stopPropagation(); // stop document selection

										if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
											return;
										}

										let command: () => Promise<void> = async () => {
											documentSettings.sidebarRightMode = SidebarRightMode.SECURITY;
											documentManager.open(this.props.portalDocument!.get().id!);
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}>
							{portalDocument.hasPasswordProtection() &&
								<Icon icon={"icon-password"}/>
							}
							{portalDocument.hasCertificateProtection() &&
								<Icon icon={"icon-certificate"}/>
							}
						</MenuButton>
					}
					{portalDocument.getAttachments().length > 0 && !portalDocument.isPortfolio() &&
						<MenuButton {...this.props} icon="icon icon-attachment small"
									tooltip={Translate.value("document_bar.attachment", {ns: "menu"})}
									onClick={async (event: React.MouseEvent) => {
										event.stopPropagation(); // stop document selection

										if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
											return;
										}

										let command: () => Promise<void> = async () => {
											if (!portalDocument.isPortfolio()) {
												documentSettings.sidebarLeftMode = SidebarLeftMode.ATTACHMENTS;
											}

											documentManager.open(this.props.portalDocument!.get().id!)
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}>
							{portalDocument.getAttachments().length}
						</MenuButton>
					}
				</div>
				<div className="column column-close">
					<MenuButton {...this.props} icon="icon icon-angle-down"
								onClick={(event: React.MouseEvent) => {
									event.stopPropagation();
								}}
								menuItems={
									[
										{
											label: Translate.value(
												"document_bar.close.document", {ns: "menu"}
											),
											icon: "icon icon-close",
											command: async () => {
												let command: () => Promise<void> = async () => {
													this.closeDocument();
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}
										},
										{
											label: Translate.value(
												"document_bar.close.delete", {ns: "menu"}
											),
											icon: "icon icon-delete small",
											disabled: !DeleteDialogDefinition.isActive(
												[portalDocument],
												portalInfo,
												this.props.signatureEditProfile
											),
											command: async () => {
												let command: () => Promise<void> = async () => {
													let parameter: Parameter<DeleteDocumentType> =
														new Parameter<DeleteDocumentType>(
															DeleteDocumentType.fromJson({
																documentIds: [documentFile!.documentId!]
															} as DeleteDocumentType)
														);

													this.props.context.getPage().setState({
														showDeleteDialog: true,
														deleteParameter: parameter
													});
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}
										}
									]
								}/>
					<MenuButton {...this.props} icon="icon icon-close small"
								tooltip={Translate.value("document_bar.close.document", {ns: "menu"})}
								onClick={async (event: React.MouseEvent) => {
									event.stopPropagation(); // stop document selection

									let command: () => Promise<void> = async () => {
										this.closeDocument();
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}/>
				</div>
				{
					(
						(this.props.contentMode === ContentMode.VIEWER && !documentSettings.selected) ||
						this.props.contentMode !== ContentMode.VIEWER
					) &&
					portalDocument.isPortfolioFile() &&
					<Tooltip target={".preview-" + tooltipId} className="tooltip-preview delay"
							 appendTo={document.getElementById("root")}>
						<div className="tooltip-content">
							<Preview {...this.props} portalDocument={Immutable.from(portalDocument.toJson())}/>
						</div>
					</Tooltip>
				}
			</div>
		);
	}
}

const DocumentBarItem = React.memo(DocumentBarItemDefinition,
	(prevProps: Readonly<DocumentBarItemProperties>, nextProps: Readonly<DocumentBarItemProperties>) => {
		if (!FunctionHelper.exists(nextProps.portalDocument)) {
			return true;
		}

		if (prevProps.contentMode !== nextProps.contentMode) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (!nextProps.portalDocuments.equals(prevProps.portalDocuments)) {
			return false;
		}

		if (!nextProps.documentSelection.equals(prevProps.documentSelection)) {
			return false;
		}

		if (!nextProps.documentSorting.equals(prevProps.documentSorting)) {
			return false;
		}

		if (!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (nextProps.showAnnotationLineFunction !== prevProps.showAnnotationLineFunction) {
			return false;
		}

		if (nextProps.showAnnotationCircleFunction !== prevProps.showAnnotationCircleFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquareFunction !== prevProps.showAnnotationSquareFunction) {
			return false;
		}

		if (nextProps.showAnnotationMarkFunction !== prevProps.showAnnotationMarkFunction) {
			return false;
		}

		if (nextProps.showAnnotationUnderlineFunction !== prevProps.showAnnotationUnderlineFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquigglyFunction !== prevProps.showAnnotationSquigglyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStrikeFunction !== prevProps.showAnnotationStrikeFunction) {
			return false;
		}

		if (nextProps.showAnnotationNoteFunction !== prevProps.showAnnotationNoteFunction) {
			return false;
		}

		if (nextProps.showRedactAreaFunction !== prevProps.showRedactAreaFunction) {
			return false;
		}

		if (nextProps.showRedactTextFunction !== prevProps.showRedactTextFunction) {
			return false;
		}

		if (nextProps.showAnnotationReplyFunction !== prevProps.showAnnotationReplyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStampFunction !== prevProps.showAnnotationStampFunction) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		return true;
	}
);

export default DocumentBarItem;